import { workspaceBlobUrl } from '@affine/core/modules/workspace/utils/blob-cache';
import {
  createCommentMutation,
  createReplyMutation,
  deleteCommentMutation,
  deleteReplyMutation,
  type DocMode,
  type ListCommentsQuery,
  listCommentsQuery,
  resolveCommentMutation,
  updateCommentMutation,
  updateReplyMutation,
  uploadCommentAttachmentMutation,
} from '@affine/graphql';
import { Entity } from '@toeverything/infra';

import type { DefaultServerService, WorkspaceServerService } from '../../cloud';
import { GraphQLService } from '../../cloud/services/graphql';
import type { WorkspaceService } from '../../workspace';
import { uploadWorkspaceFileViaTus } from '../../workspace/utils/tus-direct-upload';
import type {
  CommentTargetAnchor,
  DocComment,
  DocCommentContent,
  DocCommentListResult,
  DocCommentReply,
} from '../types';
import { findMentions } from './utils';

type GQLCommentType =
  ListCommentsQuery['workspace']['comments']['edges'][number]['node'];
type GQLReplyType = GQLCommentType['replies'][number];
type GQLUserType = GQLCommentType['user'];

// Helper functions for normalizing backend responses
const normalizeUser = (user: GQLUserType) => ({
  id: user.id,
  name: user.name,
  avatarUrl: user.avatarUrl,
});

const normalizeReply = (reply: GQLReplyType): DocCommentReply => ({
  id: reply.id,
  commentId: reply.commentId,
  content: reply.content as DocCommentContent,
  createdAt: new Date(reply.createdAt).getTime(),
  updatedAt: new Date(reply.updatedAt).getTime(),
  user: normalizeUser(reply.user),
  mentions: findMentions(reply.content.snapshot.blocks),
});

const normalizeComment = (comment: GQLCommentType): DocComment => ({
  id: comment.id,
  content: comment.content ? (comment.content as DocCommentContent) : undefined,
  resolved: comment.resolved,
  targetBlockIds: comment.targetBlockIds ?? [],
  targetAnchors: ((comment.content as DocCommentContent | undefined)
    ?.targetAnchors ?? []) as CommentTargetAnchor[],
  createdAt: new Date(comment.createdAt).getTime(),
  updatedAt: new Date(comment.updatedAt).getTime(),
  user: comment.user
    ? normalizeUser(comment.user)
    : {
        id: '',
        name: '',
        avatarUrl: '',
      },
  mentions: comment.content
    ? findMentions(comment.content.snapshot.blocks)
    : [],
  replies: comment.replies?.map(normalizeReply) ?? [],
});

export class DocCommentStore extends Entity<{
  docId: string;
  getDocMode: () => DocMode;
  getDocTitle: () => string;
}> {
  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly workspaceServerService: WorkspaceServerService,
    private readonly defaultServerService: DefaultServerService
  ) {
    super();
  }

  private get serverService() {
    return (
      this.workspaceServerService.server || this.defaultServerService.server
    );
  }

  private get graphqlService() {
    return this.serverService?.scope.get(GraphQLService);
  }

  private get currentWorkspaceId() {
    return this.workspaceService.workspace.id;
  }

  private get externalAccess() {
    return this.workspaceService.workspace.openOptions.externalDocAccess;
  }

  private get externalCommentsPath() {
    return `/api/workspaces/${encodeURIComponent(this.currentWorkspaceId)}/docs/${encodeURIComponent(this.props.docId)}/external-comments`;
  }

  private async externalRequest<T>(
    suffix = '',
    init?: RequestInit
  ): Promise<T> {
    const server = this.serverService;
    if (!server) throw new Error('Server service not found');
    const response = await server.fetch(
      `${this.externalCommentsPath}${suffix}`,
      {
        ...init,
        credentials: 'include',
        headers: {
          ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
          ...init?.headers,
        },
        streamlainMode: 'foreground',
      }
    );
    return (await response.json()) as T;
  }

  async listComments({
    after,
  }: {
    after?: string;
  }): Promise<DocCommentListResult> {
    if (this.externalAccess) {
      const query = new URLSearchParams({ first: '20' });
      if (after) query.set('after', after);
      const comments = await this.externalRequest<{
        edges: Array<{ node: GQLCommentType }>;
        pageInfo: {
          hasNextPage: boolean;
          startCursor: string | null;
          endCursor: string | null;
        };
      }>(`?${query.toString()}`);
      return {
        comments: comments.edges.map(edge => normalizeComment(edge.node)),
        hasNextPage: comments.pageInfo.hasNextPage,
        startCursor: comments.pageInfo.startCursor || '',
        endCursor: comments.pageInfo.endCursor || '',
      };
    }
    const graphql = this.graphqlService;
    if (!graphql) {
      throw new Error('GraphQL service not found');
    }
    const response = await graphql.gql({
      query: listCommentsQuery,
      variables: {
        pagination: {
          after,
        },
        workspaceId: this.currentWorkspaceId,
        docId: this.props.docId,
      },
    });

    const comments = response.workspace?.comments;
    if (!comments) {
      return {
        comments: [],
        hasNextPage: false,
        startCursor: '',
        endCursor: '',
      };
    }

    return {
      comments: comments.edges.map(edge => normalizeComment(edge.node)),
      hasNextPage: comments.pageInfo.hasNextPage,
      startCursor: comments.pageInfo.startCursor || '',
      endCursor: comments.pageInfo.endCursor || '',
    };
  }

  async createComment(commentInput: {
    content: DocCommentContent;
    mentions?: string[];
    targetBlockIds?: string[];
  }): Promise<DocComment> {
    if (this.externalAccess) {
      const comment = await this.externalRequest<GQLCommentType>('', {
        method: 'POST',
        body: JSON.stringify({
          content: commentInput.content,
          docMode: this.props.getDocMode(),
          docTitle: this.props.getDocTitle(),
          targetBlockIds: commentInput.targetBlockIds,
        }),
      });
      return normalizeComment(comment);
    }
    const graphql = this.graphqlService;
    if (!graphql) {
      throw new Error('GraphQL service not found');
    }

    const mentions = commentInput.mentions;

    const response = await graphql.gql({
      query: createCommentMutation,
      variables: {
        input: {
          workspaceId: this.currentWorkspaceId,
          docId: this.props.docId,
          docMode: this.props.getDocMode(),
          docTitle: this.props.getDocTitle(),
          content: commentInput.content,
          mentions,
          targetBlockIds: commentInput.targetBlockIds,
        },
      },
    });

    const comment = response.createComment;
    return normalizeComment(comment);
  }

  async updateComment(
    commentId: string,
    commentInput: {
      content: DocCommentContent;
    }
  ): Promise<void> {
    if (this.externalAccess) {
      await this.externalRequest(`/${encodeURIComponent(commentId)}`, {
        method: 'PATCH',
        body: JSON.stringify({ content: commentInput.content }),
      });
      return;
    }
    const graphql = this.graphqlService;
    if (!graphql) {
      throw new Error('GraphQL service not found');
    }

    await graphql.gql({
      query: updateCommentMutation,
      variables: {
        input: {
          workspaceId: this.currentWorkspaceId,
          id: commentId,
          content: commentInput.content,
        },
      },
    });
  }

  async resolveComment(commentId: string, resolved = true): Promise<boolean> {
    if (this.externalAccess) {
      return this.externalRequest<boolean>(
        `/${encodeURIComponent(commentId)}/resolved`,
        { method: 'PATCH', body: JSON.stringify({ resolved }) }
      );
    }
    const graphql = this.graphqlService;
    if (!graphql) {
      throw new Error('GraphQL service not found');
    }

    const response = await graphql.gql({
      query: resolveCommentMutation,
      variables: {
        input: {
          workspaceId: this.currentWorkspaceId,
          id: commentId,
          resolved,
        },
      },
    });

    return response.resolveComment;
  }

  async deleteComment(commentId: string): Promise<boolean> {
    if (this.externalAccess) {
      return this.externalRequest<boolean>(
        `/${encodeURIComponent(commentId)}`,
        { method: 'DELETE' }
      );
    }
    const graphql = this.graphqlService;
    if (!graphql) {
      throw new Error('GraphQL service not found');
    }

    const response = await graphql.gql({
      query: deleteCommentMutation,
      variables: {
        workspaceId: this.currentWorkspaceId,
        id: commentId,
      },
    });
    return response.deleteComment;
  }

  async createReply(
    commentId: string,
    replyInput: {
      content: DocCommentContent;
      mentions?: string[];
    }
  ): Promise<DocCommentReply> {
    if (this.externalAccess) {
      const reply = await this.externalRequest<GQLReplyType>(
        `/${encodeURIComponent(commentId)}/replies`,
        {
          method: 'POST',
          body: JSON.stringify({
            content: replyInput.content,
            docMode: this.props.getDocMode(),
            docTitle: this.props.getDocTitle(),
          }),
        }
      );
      return normalizeReply(reply);
    }
    const graphql = this.graphqlService;
    if (!graphql) {
      throw new Error('GraphQL service not found');
    }

    const response = await graphql.gql({
      query: createReplyMutation,
      variables: {
        input: {
          workspaceId: this.currentWorkspaceId,
          commentId,
          content: replyInput.content,
          docMode: this.props.getDocMode(),
          docTitle: this.props.getDocTitle(),
          mentions: replyInput.mentions,
        },
      },
    });
    return normalizeReply(response.createReply);
  }

  async updateReply(
    replyId: string,
    replyInput: {
      content: DocCommentContent;
    }
  ): Promise<void> {
    if (this.externalAccess) {
      await this.externalRequest(`/replies/${encodeURIComponent(replyId)}`, {
        method: 'PATCH',
        body: JSON.stringify({ content: replyInput.content }),
      });
      return;
    }
    const graphql = this.graphqlService;
    if (!graphql) {
      throw new Error('GraphQL service not found');
    }

    await graphql.gql({
      query: updateReplyMutation,
      variables: {
        input: {
          workspaceId: this.currentWorkspaceId,
          id: replyId,
          content: replyInput.content,
        },
      },
    });
  }

  async deleteReply(replyId: string): Promise<void> {
    if (this.externalAccess) {
      await this.externalRequest(`/replies/${encodeURIComponent(replyId)}`, {
        method: 'DELETE',
      });
      return;
    }
    const graphql = this.graphqlService;
    if (!graphql) {
      throw new Error('GraphQL service not found');
    }

    await graphql.gql({
      query: deleteReplyMutation,
      variables: {
        workspaceId: this.currentWorkspaceId,
        id: replyId,
      },
    });
  }

  /**
   * Upload a comment attachment blob and obtain the remote URL.
   * @param file File (image/blob) selected by user
   * @returns url string returned by server
   */
  async uploadCommentAttachment(file: File): Promise<string> {
    if (this.externalAccess) {
      const server = this.serverService;
      const upload = this.externalAccess.upload;
      if (!server || !upload || !upload.enabled || !upload.path) {
        throw new Error('Comment attachments are unavailable');
      }
      const blobKey = await uploadWorkspaceFileViaTus({
        workspaceId: this.currentWorkspaceId,
        context: this.workspaceService.workspace.blobContext,
        docId: this.props.docId,
        file,
        serverBaseUrl: server.baseUrl,
        tusPath: upload.path,
        externalDocAccess: true,
      });
      if (!blobKey) throw new Error('Comment attachment upload unavailable');
      return workspaceBlobUrl(this.currentWorkspaceId, blobKey);
    }
    const graphql = this.graphqlService;
    if (!graphql) {
      throw new Error('GraphQL service not found');
    }

    const res = await graphql.gql({
      timeout: 180_000,
      query: uploadCommentAttachmentMutation,
      variables: {
        workspaceId: this.currentWorkspaceId,
        docId: this.props.docId,
        attachment: file,
      },
    });
    return res.uploadCommentAttachment;
  }
}
